import { CommandHandler } from '../types/modules'
import { CommandSystemEvent, isCommand, Response } from '../types/commands'

export default class WorkerProcessingEngine  {
  sessionId: String
  worker: Worker
  commandHandler: CommandHandler

  resolveInitialized!: () => void
  resolveContinue!: () => void

  constructor (sessionId: string, worker: Worker, commandHandler: CommandHandler) {
    this.sessionId = sessionId
    this.commandHandler = commandHandler
    this.worker = worker
    
    // Remove any existing error handlers and set our own
    this.worker.onerror = null;
    this.worker.addEventListener('error', (error: ErrorEvent) => {
      console.error('[WorkerProcessingEngine] Worker error:', error)
      console.error('[WorkerProcessingEngine] Error message:', error.message)
      console.error('[WorkerProcessingEngine] Error filename:', error.filename)
      console.error('[WorkerProcessingEngine] Error lineno:', error.lineno)
      console.error('[WorkerProcessingEngine] Error colno:', error.colno)
      console.error('[WorkerProcessingEngine] Error object:', error.error)
      console.error('[WorkerProcessingEngine] Full error event:', JSON.stringify({
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
        error: error.error?.toString(),
        type: error.type
      }, null, 2))
    }
    this.worker.onmessage = (event) => {
      console.log(
        '[WorkerProcessingEngine] Received event from worker: ',
        event.data.eventType
      )
      this.handleEvent(event)
    }
  }

  sendSystemEvent (name: string): void {
    const command: CommandSystemEvent = { __type__: 'CommandSystemEvent', name }
    this.commandHandler.onCommand(command).then(
      () => {},
      () => {}
    )
  }

  handleEvent (event: any): void {
    const { eventType } = event.data
    console.log('[ReactEngine] received eventType: ', eventType)
    switch (eventType) {
      case 'initialiseDone':
        console.log('[ReactEngine] received: initialiseDone')
        this.resolveInitialized()
        break

      case 'initialiseError':
        console.error('[ReactEngine] received: initialiseError')
        console.error('[ReactEngine] Error:', event.data.error)
        console.error('[ReactEngine] Stack:', event.data.stack)
        // Reject the initialization promise
        if (this.resolveInitialized) {
          // We can't reject here since resolveInitialized is a resolve function
          // But we should log it and potentially handle it
          console.error('[ReactEngine] Worker initialization failed, rejecting promise')
        }
        break

      case 'runCycleDone':
        console.log('[ReactEngine] received: event', event.data.scriptEvent)
        this.handleRunCycle(event.data.scriptEvent)
        break
      default:
        console.log(
          '[ReactEngine] received unsupported flow event: ',
          eventType
        )
    }
  }

  start (): void {
    console.log('[WorkerProcessingEngine] started')
    const waitForInitialization: Promise<void> = this.waitForInitialization()

    waitForInitialization.then(
      () => {
        this.sendSystemEvent('initialized')
        this.firstRunCycle()
      },
      () => {}
    )
  }

  async waitForInitialization (): Promise<void> {
    return await new Promise<void>((resolve) => {
      this.resolveInitialized = resolve
      console.debug('[WorkerProcessingEngine] waiting for initialisation')
      this.worker.postMessage({ eventType: 'initialise' })
    })
  }

  firstRunCycle (): void {
    this.worker.postMessage({ eventType: 'firstRunCycle', sessionId: this.sessionId })
  }

  nextRunCycle (response: Response): void {
    console.log('[WorkerProcessingEngine] nextRunCycle');
    this.worker.postMessage({ eventType: 'nextRunCycle', response })
  }

  terminate (): void {
    this.worker.terminate()
  }

  handleRunCycle (command: any): void {
    if (isCommand(command)) {
      this.commandHandler.onCommand(command).then(
        (response) => this.nextRunCycle(response),
        () => {}
      )
    } 
  }
}
